<template>
  <div>
    <SubNav />
    <div v-if="blog" class="p-4 mt-32 mx-2 sm:mx-6 lg:mx-12 xl:mx-24">
      <!-- Back button -->
      <div class="flex items-center space-x-4 mb-10">
        <router-link to="/blogs" class="flex items-center hover:opacity-80 transition-opacity">
          <i class="fa fa-chevron-left text-navy border border-lightTangerine p-4 sm:p-4 rounded-full"
            aria-hidden="true"></i>
          <p class="font-gramatika text-lg sm:text-xl ml-2 text-navy">Back to blogs</p>
        </router-link>
      </div>

      <!-- Blog Header -->
      <div class="mb-12">
        <div class="flex flex-col sm:flex-row sm:items-baseline sm:space-x-4">
          <h1 class="text-3xl sm:text-5xl text-navy font-futura uppercase">{{ blog.title }}</h1>
          <p v-if="blog.episode" class="text-3xl sm:text-5xl text-navy font-futura">{{ blog.episode }}</p>
        </div>

        <div v-if="blog.date || blog.author" class="flex flex-wrap items-center mt-2 gap-x-2 font-gramatika">
          <template v-if="blog.date">{{ blog.date }}</template>
          <template v-if="blog.date && blog.author"> · </template>
          <template v-if="blog.author">{{ blog.author }}</template>
        </div>
      </div>

      <!-- Blog Sections -->
      <div class="space-y-6">
        <!-- Section 1 -->
        <div v-if="blog.section"
          class="font-gramatika text-sm sm:text-md p-3 w-fit border-tangerine border-2 bg-lightTangerine rounded mb-2">
          {{ blog.section }}
        </div>

        <template v-if="blog.subtitle1 || blog.content1 || blog.image1">
          <div class="section-content">
            <h2 v-if="blog.subtitle1" class="section-title !mt-2">{{ blog.subtitle1 }}</h2>
            <img v-if="blog.image1" :src="blog.image1" alt="Blog image" class="section-image" />
            <p v-if="blog.content1" class="section-text font-gramatika">{{ blog.content1 }}</p>
          </div>
        </template>

        <template v-if="blog.subtitle2 || blog.content4 || blog.image5">
          <div class="section-content">
            <h2 v-if="blog.subtitle2" class="section-title">{{ blog.subtitle2 }}</h2>
            <img v-if="blog.image5" :src="blog.image5" alt="Blog image" class="section-image" />
            <p v-if="blog.content4" class="section-text font-gramatika">{{ blog.content4 }}</p>
          </div>
        </template>

        <template v-if="blog.subtitle3 || blog.content2 || blog.image2">
          <div class="section-content">
            <h2 v-if="blog.subtitle3" class="section-title">{{ blog.subtitle3 }}</h2>
            <img v-if="blog.image2" :src="blog.image2" alt="Blog image" class="section-image" />
            <p v-if="blog.content2" class="section-text font-gramatika">{{ blog.content2 }}</p>
          </div>
        </template>

        <!-- Section 2 -->
        <div v-if="blog.section2"
          class="font-gramatika text-sm sm:text-md p-3 w-fit border-tangerine border-2 bg-lightTangerine rounded mb-2">
          {{ blog.section2 }}
        </div>

        <template v-if="blog.subtitle4 || blog.content3 || blog.image3">
          <div class="section-content">
            <h2 v-if="blog.subtitle4" class="section-title !mt-2">{{ blog.subtitle4 }}</h2>
            <img v-if="blog.image3" :src="blog.image3" alt="Blog image" class="section-image-half" />
            <p v-if="blog.content3" class="section-text font-gramatika">{{ blog.content3 }}</p>
          </div>
        </template>

        <!-- Section 3 -->
        <div v-if="blog.section3"
          class="font-gramatika text-sm sm:text-md p-3 w-fit border-tangerine border-2 bg-lightTangerine rounded mb-2">
          {{ blog.section3 }}
        </div>

        <template v-if="blog.subtitle5 || blog.content5 || blog.image4">
          <div class="section-content">
            <h2 v-if="blog.subtitle5" class="section-title !mt-2">{{ blog.subtitle5 }}</h2>
            <img v-if="blog.image4" :src="blog.image4" alt="Blog image" class="section-image-half" />
            <p v-if="blog.content5" class="section-text font-gramatika">{{ blog.content5 }}</p>
          </div>
        </template>
      </div>
    </div>

    <div v-else class="p-4 text-navy font-gramatika">
      <p>Loading blog...</p>
    </div>

    <NewsLetter />
    <footerPage />
  </div>
</template>

<script>
import client from '../contentful';
import SubNav from '@/components/SubNav.vue';
import NewsLetter from '@/components/NewsLetter.vue';
import footerPage from '@/components/footerPage.vue';

export default {
  components: { SubNav, NewsLetter, footerPage },
  data() {
    return {
      blog: null,
    };
  },
  async created() {
    const blogId = this.$route.params.id;
    try {
      const response = await client.getEntries({ content_type: "blogPost" });
      const foundBlog = response.items.find((item) => item.sys.id === blogId);
      if (foundBlog) {
        this.blog = {
          title: foundBlog.fields.title || "",
          date: foundBlog.fields.date || null,
          category: foundBlog.fields.category || null,
          author: foundBlog.fields.author || null,
          episode: foundBlog.fields.episode || null,
          section: foundBlog.fields.section1 || null,
          section2: foundBlog.fields.section2 || null,
          section3: foundBlog.fields.section3 || null,
          subtitle1: foundBlog.fields.subtitle1 || null,
          subtitle2: foundBlog.fields.subtitle2 || null,
          subtitle3: foundBlog.fields.subtitle3 || null,
          subtitle4: foundBlog.fields.subtitle4 || null,
          subtitle5: foundBlog.fields.subtitle5 || null,
          subtitle6: foundBlog.fields.subtitle6 || null,
          content1: foundBlog.fields.content1 || null,
          content2: foundBlog.fields.content2 || null,
          content3: foundBlog.fields.content3 || null,
          content4: foundBlog.fields.content4 || null,
          content5: foundBlog.fields.content5 || null,
          content6: foundBlog.fields.content6 || null,
          image: this.getImageUrl(foundBlog.fields.blogImage),
          image1: this.getImageUrl(foundBlog.fields.image1),
          image2: this.getImageUrl(foundBlog.fields.image2),
          image3: this.getImageUrl(foundBlog.fields.image3),
          image4: this.getImageUrl(foundBlog.fields.image4),
          image5: this.getImageUrl(foundBlog.fields.image5),
        };
      }
    } catch (error) {
      console.error("Error fetching blog details:", error);
    }
  },
  methods: {
    getImageUrl(imageField) {
      return imageField?.fields?.file?.url ? `https:${imageField.fields.file.url}` : null;
    }
  }
};
</script>

<style scoped>
.section-title {
  font-family: 'futura', sans-serif;
  font-size: 1.875rem;
  color: #1e3a8a;
  margin-bottom: 0.5rem;
  margin-top: 2rem;
  text-transform: uppercase;
}

@media (min-width: 640px) {
  .section-title {
    font-size: 3rem;
    line-height: 1;
  }
}

.section-image {
  width: 100%;
  height: auto;
  object-fit: cover;
  margin-top: 2rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.section-image-half {
  width: 50%;
  height: auto;
  object-fit: cover;
  margin-top: 2rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
   /* align-self: center;
 Centers the image horizontally */
}

@media (min-width: 640px) {
  .section-image {
    height: 600px;
  }

  .section-image-half {
    height: 600px;
    /* Adjust as needed for half-size images */
  }
}

.section-text {
  font-family: 'Gramatika', sans-serif;
  font-size: 1rem;
  color: #4b5563;
  text-align: justify;
  line-height: 1.625;
}

@media (min-width: 640px) {
  .section-text {
    font-size: 1.125rem;
    line-height: 1.75rem;
  }
}

.section-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.font-gramatika {
  font-family: 'Gramatika', sans-serif;
}
</style>